import DashboardEmptyState from "../../dashboard/DashboardEmptyState";
import { AccessType, UserRole, isMeDetailsFallback, useMeDetailsQuery } from 'legoland-sdk/dist/experimental';
import { Preloader } from 'llui';
import moment from 'moment';
import { useLimitDefaultsQuery, useSharedLimitsQuery } from "../api/limitsApiHooks";
import { useEffect, useState } from 'react';
import { Heading } from 'tombac';
import { useUserDetails } from "../../users/UserDetailsProvider";
import ProductForm from "./ProductForm";
import { ProductPackages } from "./ProductPackages";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const productUpdateFromFormValues = productListState => {
  const allProducts = Object.keys(productListState).map(key => productListState[key]);
  const resell = allProducts.filter(p => p.resell === true).map(p => ({
    id: p.id,
    expiryDate: p.resellUntil,
    productId: p.id
  }));
  const access = allProducts.filter(p => p.access === true).map(p => ({
    id: p.id,
    productId: p.id,
    expiryDate: p.expiryDate,
    accessType: p.accessType,
    apiKey: p.apiKey
  }));
  return {
    access,
    resell
  };
};
const emptyValues = {};
function ProductFormsContainer(_ref) {
  let {
    initialValues = emptyValues,
    newUser = false,
    onChange,
    userRole,
    hasUserContracts
  } = _ref;
  const meDetailsQuery = useMeDetailsQuery();
  const meDetails = meDetailsQuery.data?.meDetails;
  const creatorRole = meDetails?.role;
  const availableProducts = meDetails?.products;
  const {
    blockOldAccessProcess
  } = useUserDetails();
  const [values, setValues] = useState(initialValues);
  useEffect(() => {
    if (!availableProducts || isMeDetailsFallback(meDetails)) return;
    const values = {
      ...initialValues
    };
    availableProducts.forEach(p => {
      if (creatorRole === UserRole.Distributor && p.resell.status !== 'ALLOWED') {
        delete values[p.id];
        return;
      }
      if (values[p.id] !== undefined) return;
      values[p.id] = {
        id: p.id,
        pending: false,
        displayName: p.displayName,
        expired: false,
        expiryDate: null,
        accessType: AccessType.Paying,
        access: false,
        resell: false,
        resellUntil: null,
        revoked: false,
        productType: p.productType
      };
    });
    setValues(oldValues => JSON.stringify(oldValues) === JSON.stringify(values) ? oldValues : values);
  }, [availableProducts?.length, creatorRole, meDetails?.email, initialValues, meDetails && isMeDetailsFallback(meDetails)]);
  const defaults = useLimitDefaultsQuery().data?.defaults;
  useEffect(() => {
    const newValues = {
      ...values
    };
    let updated = false;
    if (defaults) {
      for (const pid in newValues) {
        const defaultEvaluationLengthInDays = defaults.find(i => i.productId === pid && i.accessType === AccessType.Evaluation)?.limit.defaultEvaluationLengthInDays;
        if (defaultEvaluationLengthInDays && newValues[pid].expiryDate === null) {
          newValues[pid].expiryDate = moment(new Date()).add(defaultEvaluationLengthInDays, 'days').toDate();
          updated = true;
        }
      }
    }
    if (updated) setValues(newValues);
  }, [defaults, values]);
  const productIds = Object.keys(values);
  const canSeePackages = productIds.includes('ts') && productIds.includes('od');
  const canGiveResell = creatorRole === UserRole.Admin && !hasUserContracts && !blockOldAccessProcess;
  const canGiveAccess = (creatorRole === UserRole.Admin || creatorRole === UserRole.Distributor || newUser) && !hasUserContracts && !blockOldAccessProcess;
  const canSeeApiKey = !newUser && (creatorRole === UserRole.Admin || creatorRole === UserRole.Support);
  const userCanResell = userRole === UserRole.Distributor;
  const canGiveInternalProduct = creatorRole === UserRole.Admin;
  const canGiveFreeProduct = creatorRole === UserRole.Admin;
  const canSetCustomAgreement = creatorRole === UserRole.Admin;
  const canSetLimits = !newUser && (creatorRole === UserRole.Admin || creatorRole === UserRole.Distributor) && !hasUserContracts && !blockOldAccessProcess;
  useEffect(() => {
    onChange(values);
  }, [values]);
  const sharedLimitsQuery = useSharedLimitsQuery(undefined, {
    enabled: !newUser && (creatorRole === UserRole.Admin || creatorRole === UserRole.Distributor)
  });
  const packagedIds = sharedLimitsQuery.data?.limits && canSeePackages ? ['od', 'ts'] : [];
  const regularIds = productIds.filter(id => !packagedIds.includes(id)).sort();
  if (!availableProducts || sharedLimitsQuery.isLoading) {
    return /*#__PURE__*/_jsx(Preloader, {});
  }
  const renderProduct = id => /*#__PURE__*/_jsx(ProductForm, {
    isInitialSetup: newUser,
    onChange: p => setValues(values => ({
      ...values,
      [id]: typeof p === 'function' ? p(values[id]) : p
    })),
    packaged: packagedIds.includes(id),
    showResell: canGiveResell && userCanResell,
    showAccess: canGiveAccess,
    showProductDetails: values[id].access || initialValues[id] && (initialValues[id].access || initialValues[id].revoked),
    showApiKey: canSeeApiKey,
    editableLimits: canSetLimits,
    showInternalOption: canGiveInternalProduct,
    showFreeOption: canGiveFreeProduct,
    showCustomAgreementOption: canSetCustomAgreement,
    values: values[id],
    hasUserContracts: hasUserContracts,
    "data-sentry-element": "ProductForm",
    "data-sentry-component": "renderProduct",
    "data-sentry-source-file": "ProductFormsContainer.tsx"
  }, id);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!newUser && canSeePackages && /*#__PURE__*/_jsx(ProductPackages, {
      hasUserContracts: hasUserContracts,
      children: packagedIds.map(renderProduct)
    }), /*#__PURE__*/_jsx(Heading, {
      level: 3,
      "data-sentry-element": "Heading",
      "data-sentry-source-file": "ProductFormsContainer.tsx",
      "data-sentry-element": "Heading",
      children: "Products"
    }), /*#__PURE__*/_jsxs("div", {
      className: "ProductList",
      children: [regularIds.length === 0 && /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(DashboardEmptyState, {})
      }), regularIds.map(renderProduct)]
    })]
  });
}
export default ProductFormsContainer;